import React from "react";
import moment from "moment";
import {
  Layout,
  Card,
  Row,
  Col,
  Tag,
  Badge,
  Button,
  Input,
  Select,
  DatePicker,
  Dropdown,
  Menu,
  Tooltip,
  Icon,
  Divider,
  message,
  Checkbox,
  Empty,
  Drawer,
  Typography,
  Descriptions,
  Alert,
  Upload
} from "antd";
import { getUserData } from "./utils/authService";

const { Content } = Layout;
const { Option } = Select;
const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const ALERT_TYPES = [
  "Document Renewal",
  "Full Tank to Full Tank Mileage",
  "IoT Based Mileage",
  "Asset Audit Due",
  "Manpower Entry Pending"
];

// Color mapping for alert types
const getAlertTypeColor = (alertType) => {
  const colorMap = {
    "Document Renewal": { bg: "#e9f2ff", color: "#0747a6" }, // Blue
    "Full Tank to Full Tank Mileage": { bg: "#fff4e6", color: "#d46b08" }, // Orange
    "IoT Based Mileage": { bg: "#f6ffed", color: "#389e0d" }, // Green
    "Asset Audit Due": { bg: "#fff0f6", color: "#c41d7f" }, // Pink/Magenta
    "Manpower Entry Pending": { bg: "#f0f5ff", color: "#2f54eb" } // Indigo
  };
  
  return colorMap[alertType] || { bg: "#f4f5f7", color: "#42526e" }; // Default grey
};

const STATUS_COLUMNS = [
  { id: "New", label: "New", subtitle: "Unread" },
  { id: "Under Analysis", label: "Under Analysis", subtitle: "In progress" },
  { id: "Action Pending", label: "Action Pending", subtitle: "Awaiting action" }
];

const SEVERITY = ["Info", "Warning", "Critical"];

// --- helpers ---
function severityTag(sev) {
  if (sev === "Critical") return <Tag color="red">CRITICAL</Tag>;
  if (sev === "Warning") return <Tag color="orange">WARNING</Tag>;
  return <Tag color="blue">INFO</Tag>;
}

function isOverdue(record) {
  if (!record.dueAt) return false;
  return moment(record.dueAt).isBefore(moment(), 'day');
}

function isDue(record) {
  if (!record.dueAt) return false;
  return moment(record.dueAt).isSame(moment(), 'day');
}

function getDueStatus(record) {
  if (!record.dueAt) return null;
  const dueDate = moment(record.dueAt);
  const now = moment();
  
  if (dueDate.isBefore(now, 'day')) {
    return { status: 'overdue', days: now.diff(dueDate, 'days') };
  } else if (dueDate.isSame(now, 'day')) {
    return { status: 'due', days: 0 };
  } else {
    const daysUntil = dueDate.diff(now, 'days');
    return { status: 'upcoming', days: daysUntil };
  }
}

function humanAge(ts) {
  return moment(ts).fromNow();
}

export default class AlertInbox extends React.Component {
  state = {
    loading: false,
    filters: {
      q: ""
    },
    activeFilterTypes: ["alertType"], // Array of active filter types, default to alertType only
    enabledFilterValues: {}, // Object mapping filterType -> array of enabled values
    expandedColumn: null,
    drawerVisible: false,
    drawerExpanded: false,
    selectedAlert: null,
    analysisText: "",
    actionSuggestedText: "",
    actionTakenText: "",
    fileList: [],
    data: [
      {
        id: "AL-10291",
        severity: "Critical",
        type: "Document Renewal",
        title: "Insurance expiring in 10 days",
        body: "<p>The insurance policy for asset <strong>GSE-APU-014 (APU-014)</strong> is set to expire on <strong>28 December 2025</strong>. This is a critical document that requires immediate renewal to ensure compliance and avoid operational disruptions.</p><p><strong>Asset Details:</strong></p><ul><li><strong>Location:</strong> DEL Terminal 3</li><li><strong>Owner:</strong> OWN-001</li></ul><p><strong>Action Required:</strong> Please initiate the renewal process immediately to prevent any service interruptions.</p>",
        entityLabel: "Asset: GSE-APU-014",
        entityId: "GSE-APU-014",
        assetCode: "GSE-APU-014",
        ownerCode: "OWN-001",
        altName: "APU-014",
        location: "DEL",
        subLocation: "Terminal 3",
        assetModel: "Ground Support Equipment",
        assetSubType: "Aircraft Power Unit",
        site: "DEL",
        status: "New",
        assignee: "Unassigned",
        createdAt: moment().subtract(2, "hours"),
        dueAt: moment().add(1, "day"),
        analysisStarted: false,
        analysisComplete: false,
        statusHistory: []
      },
      {
        id: "AL-10286",
        severity: "Warning",
        type: "IoT Based Mileage",
        title: "IoT mileage deviates from manual entry by 18%",
        body: "<p>The IoT sensor reading for vehicle <strong>DL-01-AB-2233 (VEH-2233)</strong> shows a <strong>significant deviation of 18%</strong> from the manually entered mileage data.</p><p><strong>Mileage Comparison:</strong></p><ul><li><strong>IoT System:</strong> 12,450 km</li><li><strong>Manual Entry:</strong> 10,500 km</li><li><strong>Deviation:</strong> 1,950 km (18%)</li></ul><p><strong>Vehicle Details:</strong></p><ul><li><strong>Location:</strong> BOM Hangar 2</li><li><strong>Fleet:</strong> FLEET-001</li></ul><p><strong>Investigation Required:</strong> This discrepancy requires investigation to determine if there's a sensor calibration issue, data entry error, or potential tampering.</p>",
        entityLabel: "Vehicle: DL-01-AB-2233",
        entityId: "DL-01-AB-2233",
        assetCode: "DL-01-AB-2233",
        ownerCode: "FLEET-001",
        altName: "VEH-2233",
        location: "BOM",
        subLocation: "Hangar 2",
        assetModel: "Commercial Vehicle",
        assetSubType: "Truck",
        site: "BOM",
        status: "Under Analysis",
        assignee: "Ops Supervisor",
        createdAt: moment().subtract(2, "days"),
        dueAt: moment().subtract(4, "hours"),
        analysisStarted: true,
        analysisComplete: false,
        statusHistory: [
          { 
            from: "New", 
            to: "Under Analysis", 
            action: "Start Analysis", 
            timestamp: moment().subtract(2, "days"),
            timestampFormatted: moment().subtract(2, "days").format("DD MMM YYYY, HH:mm:ss"),
            userId: "user1",
            durationInPreviousStatus: {
              minutes: 120,
              hours: 2,
              days: 0,
              humanReadable: "2 hours"
            }
          }
        ]
      },
      {
        id: "AL-10270",
        severity: "Info",
        type: "Manpower Entry Pending",
        title: "Manpower entry pending for Shift B (yesterday)",
        body: "<p>The manpower entry for <strong>Shift B</strong> at <strong>HYD Main Office</strong> site was not completed for yesterday's shift.</p><p><strong>Shift Details:</strong></p><ul><li><strong>Shift:</strong> Shift B</li><li><strong>Time:</strong> 14:00 to 22:00</li><li><strong>Location:</strong> HYD Main Office</li><li><strong>Date:</strong> Yesterday</li></ul><p><strong>Required Information:</strong></p><ul><li>All personnel present</li><li>Their roles</li><li>Hours worked</li></ul><p><strong>Purpose:</strong> This entry is required for accurate payroll processing, attendance tracking, and compliance reporting. Please complete this entry at your earliest convenience.</p>",
        entityLabel: "Site: HYD",
        entityId: "HYD",
        assetCode: null,
        ownerCode: null,
        altName: null,
        location: "HYD",
        subLocation: "Main Office",
        assetModel: null,
        assetSubType: null,
        site: "HYD",
        status: "Action Pending",
        assignee: "Site Admin",
        createdAt: moment().subtract(4, "days"),
        dueAt: moment().add(2, "days"),
        analysisStarted: true,
        analysisComplete: true,
        statusHistory: [
          { 
            from: "New", 
            to: "Under Analysis", 
            action: "Start Analysis", 
            timestamp: moment().subtract(4, "days"),
            timestampFormatted: moment().subtract(4, "days").format("DD MMM YYYY, HH:mm:ss"),
            userId: "user1",
            durationInPreviousStatus: {
              minutes: 30,
              hours: 0,
              days: 0,
              humanReadable: "30 minutes"
            }
          },
          { 
            from: "Under Analysis", 
            to: "Action Pending", 
            action: "Complete Analysis", 
            timestamp: moment().subtract(3, "days"),
            timestampFormatted: moment().subtract(3, "days").format("DD MMM YYYY, HH:mm:ss"),
            userId: "user1",
            durationInPreviousStatus: {
              minutes: 1440,
              hours: 24,
              days: 1,
              humanReadable: "1 day"
            }
          }
        ]
      },
      {
        id: "AL-10301",
        severity: "Warning",
        type: "Full Tank to Full Tank Mileage",
        title: "Calculated mileage is outside the defined threshold",
        body: "<p>The calculated mileage for vehicle <strong>COV-332 (AS-01-MC-1334 / DURO-332)</strong> shows a <strong>significant deviation of 24.18%</strong> from the standard consumption rate.</p><p><strong>Refueling Period:</strong></p><ul><li><strong>Last Full Tank:</strong> 20-12-2025</li><li><strong>Current Refueling:</strong> 25-12-2025</li></ul><p><strong>Consumption Analysis:</strong></p><ul><li><strong>Actual Rate:</strong> 0.11 Qty/KMS (8.86 KMS/Qty)</li><li><strong>Standard Rate:</strong> 0.09 Qty/KMS (11.00 KMS/Qty)</li><li><strong>Deviation:</strong> 24.18%</li></ul><p><strong>Vehicle Performance:</strong></p><ul><li><strong>Distance Covered:</strong> 401.00 KMS</li><li><strong>Fuel Added:</strong> 45.27 litres</li></ul><p><strong>Possible Causes:</strong> This significant deviation may indicate fuel loss, inefficient driving patterns, or potential mechanical issues requiring investigation.</p>",
        entityLabel: "Asset: COV-332 / AS-01-MC-1334 / DURO-332",
        entityId: "COV-332",
        assetCode: "COV-332",
        altName: "AS-01-MC-1334",
        ownerCode: "DURO-332",
        location: "N/A",
        subLocation: null,
        assetModel: "TATA DI",
        assetSubType: "Commercial Vehicle",
        site: "N/A",
        status: "New",
        assignee: "Unassigned",
        createdAt: moment("2025-12-25 05:46:39", "YYYY-MM-DD HH:mm:ss"),
        dueAt: moment("2025-12-25 05:46:39", "YYYY-MM-DD HH:mm:ss").add(7, "days"),
        analysisStarted: false,
        analysisComplete: false,
        statusHistory: [],
        // Detailed fuel alert data
        alertDetails: {
          assetCode: "COV-332",
          altName: "AS-01-MC-1334",
          ownerCode: "DURO-332",
          assetModel: "TATA DI",
          assetSubType: "Commercial Vehicle",
          voucherNo: "",
          refuelingDate: "25-12-2025 05:46:39 GMT",
          locationOfRefueling: "N/A",
          fuelAdded: "45.27 litre",
          refueledOdometerReading: "147328 KMS (147328 KMS)",
          lastFullTankOdometerReading: "146927 KMS (146927 KMS)",
          lastFullTankDate: "20-12-2025 07:18:58 GMT",
          kmsRun: "401.00 KMS",
          consumptionRateCalculation: "0.11 Qty/KMS (8.86 KMS/Qty)",
          standardConsumptionRate: "0.09 Qty/KMS (11.00 KMS/Qty)",
          deviationFromStandard: "24.18% KMS",
          action: "Please analyze for fuel loss here."
        }
      },
      {
        id: "AL-10302",
        severity: "Critical",
        type: "Document Renewal",
        title: "License expiring in 5 days",
        body: "<p>The operating license for asset <strong>GSE-APU-015 (APU-015)</strong> is set to expire in <strong>5 days</strong>. This is a critical document that requires immediate renewal to ensure compliance with regulatory requirements.</p><p><strong>Asset Details:</strong></p><ul><li><strong>Location:</strong> BOM Terminal 1</li><li><strong>Owner:</strong> OWN-002</li></ul><p><strong>⚠️ Important:</strong> Failure to renew this license may result in operational restrictions or penalties.</p>",
        entityLabel: "Asset: GSE-APU-015",
        entityId: "GSE-APU-015",
        assetCode: "GSE-APU-015",
        ownerCode: "OWN-002",
        altName: "APU-015",
        location: "BOM",
        subLocation: "Terminal 1",
        assetType: "Ground Support Equipment",
        assetModel: "Ground Support Equipment",
        assetSubType: "Aircraft Power Unit",
        site: "BOM",
        status: "New",
        assignee: "Unassigned",
        createdAt: moment().subtract(1, "hour"),
        dueAt: moment().add(5, "days"),
        analysisStarted: false,
        analysisComplete: false,
        statusHistory: []
      },
      {
        id: "AL-10303",
        severity: "Warning",
        type: "IoT Based Mileage",
        title: "IoT mileage deviates from manual entry by 22%",
        body: "<p>The IoT sensor reading for vehicle <strong>DL-01-AB-2234 (VEH-2234)</strong> shows a <strong>significant deviation of 22%</strong> from the manually entered mileage data.</p><p><strong>Mileage Comparison:</strong></p><ul><li><strong>IoT System:</strong> 15,200 km</li><li><strong>Manual Entry:</strong> 12,450 km</li><li><strong>Deviation:</strong> 2,750 km (22%)</li></ul><p><strong>Vehicle Details:</strong></p><ul><li><strong>Location:</strong> DEL Hangar 1</li><li><strong>Fleet:</strong> FLEET-002</li></ul><p><strong>⚠️ Immediate Action Required:</strong> This large discrepancy requires immediate investigation to determine if there's a sensor calibration issue, data entry error, or potential tampering.</p>",
        entityLabel: "Vehicle: DL-01-AB-2234",
        entityId: "DL-01-AB-2234",
        assetCode: "DL-01-AB-2234",
        ownerCode: "FLEET-002",
        altName: "VEH-2234",
        location: "DEL",
        subLocation: "Hangar 1",
        assetType: "Commercial Vehicle",
        assetModel: "Commercial Vehicle",
        assetSubType: "Truck",
        site: "DEL",
        status: "New",
        assignee: "Unassigned",
        createdAt: moment().subtract(3, "hours"),
        dueAt: moment().add(3, "days"),
        analysisStarted: false,
        analysisComplete: false,
        statusHistory: []
      },
      {
        id: "AL-10304",
        severity: "Info",
        type: "Manpower Entry Pending",
        title: "Manpower entry pending for Shift A (today)",
        body: "<p>The manpower entry for <strong>Shift A</strong> at <strong>HYD Main Office</strong> site has not been completed for today's shift.</p><p><strong>Shift Details:</strong></p><ul><li><strong>Shift:</strong> Shift A</li><li><strong>Time:</strong> 06:00 to 14:00</li><li><strong>Location:</strong> HYD Main Office</li><li><strong>Date:</strong> Today</li></ul><p><strong>Required Information:</strong></p><ul><li>All personnel present</li><li>Their roles</li><li>Hours worked</li></ul><p><strong>Purpose:</strong> This entry is required for accurate payroll processing, attendance tracking, and compliance reporting. Please complete this entry at your earliest convenience.</p>",
        entityLabel: "Site: HYD",
        entityId: "HYD",
        assetCode: null,
        ownerCode: null,
        altName: null,
        location: "HYD",
        subLocation: "Main Office",
        assetType: null,
        assetModel: null,
        assetSubType: null,
        site: "HYD",
        status: "New",
        assignee: "Unassigned",
        createdAt: moment().subtract(30, "minutes"),
        dueAt: moment().add(1, "day"),
        analysisStarted: false,
        analysisComplete: false,
        statusHistory: []
      },
      {
        id: "AL-10305",
        severity: "Critical",
        type: "Full Tank to Full Tank Mileage",
        title: "Calculated mileage is outside the defined threshold",
        body: "<p>The calculated mileage for vehicle <strong>COV-333 (AS-01-MC-1335 / DURO-333)</strong> shows a <strong>critical deviation</strong> from the standard consumption rate.</p><p><strong>Issue:</strong> The vehicle consumed fuel at a rate significantly higher than expected.</p><p><strong>Possible Causes:</strong></p><ul><li>Potential fuel loss</li><li>Inefficient driving patterns</li><li>Mechanical issues</li></ul><p><strong>Vehicle Location:</strong> BOM Warehouse</p><p><strong>⚠️ Immediate Action Required:</strong> Requires immediate investigation to identify the root cause and prevent further fuel wastage.</p>",
        entityLabel: "Asset: COV-333 / AS-01-MC-1335 / DURO-333",
        entityId: "COV-333",
        assetCode: "COV-333",
        altName: "AS-01-MC-1335",
        ownerCode: "DURO-333",
        location: "BOM",
        subLocation: "Warehouse",
        assetType: "Commercial Vehicle",
        assetModel: "TATA DI",
        assetSubType: "Commercial Vehicle",
        site: "BOM",
        status: "New",
        assignee: "Unassigned",
        createdAt: moment().subtract(2, "hours"),
        dueAt: moment().add(6, "days"),
        analysisStarted: false,
        analysisComplete: false,
        statusHistory: []
      },
      {
        id: "AL-10306",
        severity: "Warning",
        type: "Document Renewal",
        title: "Insurance expiring in 15 days",
        body: "<p>The insurance policy for asset <strong>GSE-APU-016 (APU-016)</strong> is set to expire in <strong>15 days</strong>. This document requires renewal to ensure continued coverage and compliance.</p><p><strong>Asset Details:</strong></p><ul><li><strong>Location:</strong> HYD Terminal 2</li><li><strong>Owner:</strong> OWN-003</li></ul><p><strong>Action Required:</strong> Please initiate the renewal process to avoid any coverage gaps.</p>",
        entityLabel: "Asset: GSE-APU-016",
        entityId: "GSE-APU-016",
        assetCode: "GSE-APU-016",
        ownerCode: "OWN-003",
        altName: "APU-016",
        location: "HYD",
        subLocation: "Terminal 2",
        assetType: "Ground Support Equipment",
        assetModel: "Ground Support Equipment",
        assetSubType: "Aircraft Power Unit",
        site: "HYD",
        status: "New",
        assignee: "Unassigned",
        createdAt: moment().subtract(45, "minutes"),
        dueAt: moment().add(15, "days"),
        analysisStarted: false,
        analysisComplete: false,
        statusHistory: []
      },
      {
        id: "AL-10307",
        severity: "Info",
        type: "IoT Based Mileage",
        title: "IoT mileage deviates from manual entry by 12%",
        body: "<p>The IoT sensor reading for vehicle <strong>DL-01-AB-2235 (VEH-2235)</strong> shows a deviation of <strong>12%</strong> from the manually entered mileage data.</p><p><strong>Mileage Comparison:</strong></p><ul><li><strong>IoT System:</strong> 8,900 km</li><li><strong>Manual Entry:</strong> 7,950 km</li><li><strong>Deviation:</strong> 950 km (12%)</li></ul><p><strong>Vehicle Details:</strong></p><ul><li><strong>Location:</strong> DEL Hangar 3</li><li><strong>Fleet:</strong> FLEET-003</li></ul><p><strong>Action Required:</strong> This moderate discrepancy should be investigated to ensure data accuracy.</p>",
        entityLabel: "Vehicle: DL-01-AB-2235",
        entityId: "DL-01-AB-2235",
        assetCode: "DL-01-AB-2235",
        ownerCode: "FLEET-003",
        altName: "VEH-2235",
        location: "DEL",
        subLocation: "Hangar 3",
        assetType: "Commercial Vehicle",
        assetModel: "Commercial Vehicle",
        assetSubType: "Van",
        site: "DEL",
        status: "New",
        assignee: "Unassigned",
        createdAt: moment().subtract(15, "minutes"),
        dueAt: moment().add(4, "days"),
        analysisStarted: false,
        analysisComplete: false,
        statusHistory: []
      },
      {
        id: "AL-10308",
        severity: "Critical",
        type: "Full Tank to Full Tank Mileage",
        title: "Calculated mileage is outside the defined threshold",
        body: "<p>The calculated mileage for vehicle <strong>COV-334 (AS-01-MC-1336 / DURO-334)</strong> shows a <strong>critical deviation</strong> from the standard consumption rate.</p><p><strong>Issue:</strong> The vehicle consumed fuel at a rate significantly higher than expected.</p><p><strong>Possible Causes:</strong></p><ul><li>Potential fuel loss</li><li>Inefficient driving patterns</li><li>Mechanical issues</li></ul><p><strong>Vehicle Location:</strong> BOM Maintenance Bay</p><p><strong>⚠️ Immediate Action Required:</strong> Requires immediate investigation.</p>",
        entityLabel: "Asset: COV-334 / AS-01-MC-1336 / DURO-334",
        entityId: "COV-334",
        assetCode: "COV-334",
        altName: "AS-01-MC-1336",
        ownerCode: "DURO-334",
        location: "BOM",
        subLocation: "Maintenance Bay",
        assetType: "Commercial Vehicle",
        assetModel: "TATA DI",
        assetSubType: "Commercial Vehicle",
        site: "BOM",
        status: "New",
        assignee: "Unassigned",
        createdAt: moment().subtract(1, "day"),
        dueAt: moment().add(2, "days"),
        analysisStarted: false,
        analysisComplete: false,
        statusHistory: []
      },
      {
        id: "AL-10309",
        severity: "Warning",
        type: "Manpower Entry Pending",
        title: "Manpower entry pending for Shift C (yesterday)",
        body: "<p>The manpower entry for <strong>Shift C</strong> at <strong>DEL Control Room</strong> site was not completed for yesterday's shift.</p><p><strong>Shift Details:</strong></p><ul><li><strong>Shift:</strong> Shift C</li><li><strong>Time:</strong> 22:00 to 06:00</li><li><strong>Location:</strong> DEL Control Room</li><li><strong>Date:</strong> Yesterday</li></ul><p><strong>Required Information:</strong></p><ul><li>All personnel present</li><li>Their roles</li><li>Hours worked</li></ul><p><strong>Purpose:</strong> This entry is required for accurate payroll processing, attendance tracking, and compliance reporting. Please complete this entry as soon as possible.</p>",
        entityLabel: "Site: DEL",
        entityId: "DEL",
        assetCode: null,
        ownerCode: null,
        altName: null,
        location: "DEL",
        subLocation: "Control Room",
        assetType: null,
        assetModel: null,
        assetSubType: null,
        site: "DEL",
        status: "New",
        assignee: "Unassigned",
        createdAt: moment().subtract(4, "hours"),
        dueAt: moment().add(1, "day"),
        analysisStarted: false,
        analysisComplete: false,
        statusHistory: []
      },
      {
        id: "AL-10310",
        severity: "Info",
        type: "Document Renewal",
        title: "Permit expiring in 20 days",
        body: "<p>The operating permit for asset <strong>GSE-APU-017 (APU-017)</strong> is set to expire in <strong>20 days</strong>. This permit is required for the asset to operate legally within the facility.</p><p><strong>Asset Details:</strong></p><ul><li><strong>Location:</strong> HYD Terminal 1</li><li><strong>Owner:</strong> OWN-004</li></ul><p><strong>Action Required:</strong> Please initiate the renewal process to ensure continued operations.</p>",
        entityLabel: "Asset: GSE-APU-017",
        entityId: "GSE-APU-017",
        assetCode: "GSE-APU-017",
        ownerCode: "OWN-004",
        altName: "APU-017",
        location: "HYD",
        subLocation: "Terminal 1",
        assetType: "Ground Support Equipment",
        assetModel: "Ground Support Equipment",
        assetSubType: "Aircraft Power Unit",
        site: "HYD",
        status: "New",
        assignee: "Unassigned",
        createdAt: moment().subtract(20, "minutes"),
        dueAt: moment().add(20, "days"),
        analysisStarted: false,
        analysisComplete: false,
        statusHistory: []
      },
      {
        id: "AL-10311",
        severity: "Critical",
        type: "IoT Based Mileage",
        title: "IoT mileage deviates from manual entry by 28%",
        body: "<p>The IoT sensor reading for vehicle <strong>DL-01-AB-2236 (VEH-2236)</strong> shows a <strong>critical deviation of 28%</strong> from the manually entered mileage data.</p><p><strong>Mileage Comparison:</strong></p><ul><li><strong>IoT System:</strong> 18,500 km</li><li><strong>Manual Entry:</strong> 14,450 km</li><li><strong>Deviation:</strong> 4,050 km (28%)</li></ul><p><strong>Vehicle Details:</strong></p><ul><li><strong>Location:</strong> BOM Hangar 2</li><li><strong>Fleet:</strong> FLEET-004</li></ul><p><strong>⚠️ Immediate Action Required:</strong> This large discrepancy requires immediate investigation to determine if there's a sensor calibration issue, data entry error, or potential tampering.</p>",
        entityLabel: "Vehicle: DL-01-AB-2236",
        entityId: "DL-01-AB-2236",
        assetCode: "DL-01-AB-2236",
        ownerCode: "FLEET-004",
        altName: "VEH-2236",
        location: "BOM",
        subLocation: "Hangar 2",
        assetType: "Commercial Vehicle",
        assetModel: "Commercial Vehicle",
        assetSubType: "Bus",
        site: "BOM",
        status: "New",
        assignee: "Unassigned",
        createdAt: moment().subtract(5, "hours"),
        dueAt: moment().add(1, "day"),
        analysisStarted: false,
        analysisComplete: false,
        statusHistory: []
      },
      {
        id: "AL-10312",
        severity: "Warning",
        type: "Full Tank to Full Tank Mileage",
        title: "Calculated mileage is outside the defined threshold",
        body: "<p>The calculated mileage for vehicle <strong>COV-335 (AS-01-MC-1337 / DURO-335)</strong> shows a deviation from the standard consumption rate.</p><p><strong>Issue:</strong> The vehicle consumed fuel at a rate higher than expected.</p><p><strong>Possible Causes:</strong></p><ul><li>Potential fuel loss</li><li>Inefficient driving patterns</li><li>Mechanical issues</li></ul><p><strong>Vehicle Location:</strong> DEL Parking Area</p><p><strong>Action Required:</strong> Requires investigation to identify the root cause.</p>",
        entityLabel: "Asset: COV-335 / AS-01-MC-1337 / DURO-335",
        entityId: "COV-335",
        assetCode: "COV-335",
        altName: "AS-01-MC-1337",
        ownerCode: "DURO-335",
        location: "DEL",
        subLocation: "Parking Area",
        assetType: "Commercial Vehicle",
        assetModel: "TATA DI",
        assetSubType: "Commercial Vehicle",
        site: "DEL",
        status: "New",
        assignee: "Unassigned",
        createdAt: moment().subtract(10, "minutes"),
        dueAt: moment().add(8, "days"),
        analysisStarted: false,
        analysisComplete: false,
        statusHistory: []
      },
      {
        id: "AL-10313",
        severity: "Info",
        type: "Document Renewal",
        title: "Certificate expiring in 30 days",
        body: "<p>The compliance certificate for asset <strong>GSE-APU-018 (APU-018)</strong> is set to expire in <strong>30 days</strong>. This certificate is required to demonstrate compliance with safety and operational standards.</p><p><strong>Asset Details:</strong></p><ul><li><strong>Location:</strong> BOM Terminal 3</li><li><strong>Owner:</strong> OWN-005</li></ul><p><strong>Action Required:</strong> Please initiate the renewal process to ensure continued compliance.</p>",
        entityLabel: "Asset: GSE-APU-018",
        entityId: "GSE-APU-018",
        assetCode: "GSE-APU-018",
        ownerCode: "OWN-005",
        altName: "APU-018",
        location: "BOM",
        subLocation: "Terminal 3",
        assetType: "Ground Support Equipment",
        assetModel: "Ground Support Equipment",
        assetSubType: "Aircraft Power Unit",
        site: "BOM",
        status: "New",
        assignee: "Unassigned",
        createdAt: moment().subtract(6, "hours"),
        dueAt: moment().add(30, "days"),
        analysisStarted: false,
        analysisComplete: false,
        statusHistory: []
      },
      {
        id: "AL-10314",
        severity: "Critical",
        type: "Manpower Entry Pending",
        title: "Manpower entry pending for Shift B (today)",
        body: "<p>The manpower entry for <strong>Shift B</strong> at <strong>BOM Operations Center</strong> site has not been completed for today's shift.</p><p><strong>Shift Details:</strong></p><ul><li><strong>Shift:</strong> Shift B</li><li><strong>Time:</strong> 14:00 to 22:00</li><li><strong>Location:</strong> BOM Operations Center</li><li><strong>Date:</strong> Today</li></ul><p><strong>Required Information:</strong></p><ul><li>All personnel present</li><li>Their roles</li><li>Hours worked</li></ul><p><strong>⚠️ Critical:</strong> This entry is critical for accurate payroll processing, attendance tracking, and compliance reporting. Please complete this entry immediately.</p>",
        entityLabel: "Site: BOM",
        entityId: "BOM",
        assetCode: null,
        ownerCode: null,
        altName: null,
        location: "BOM",
        subLocation: "Operations Center",
        assetType: null,
        assetModel: null,
        assetSubType: null,
        site: "BOM",
        status: "New",
        assignee: "Unassigned",
        createdAt: moment().subtract(25, "minutes"),
        dueAt: moment().add(1, "day"),
        analysisStarted: false,
        analysisComplete: false,
        statusHistory: []
      },
      {
        id: "AL-10315",
        severity: "Warning",
        type: "IoT Based Mileage",
        title: "IoT mileage deviates from manual entry by 15%",
        body: "<p>The IoT sensor reading for vehicle <strong>DL-01-AB-2237 (VEH-2237)</strong> shows a deviation of <strong>15%</strong> from the manually entered mileage data.</p><p><strong>Mileage Comparison:</strong></p><ul><li><strong>IoT System:</strong> 11,200 km</li><li><strong>Manual Entry:</strong> 9,750 km</li><li><strong>Deviation:</strong> 1,450 km (15%)</li></ul><p><strong>Vehicle Details:</strong></p><ul><li><strong>Location:</strong> HYD Hangar 1</li><li><strong>Fleet:</strong> FLEET-005</li></ul><p><strong>Action Required:</strong> This moderate discrepancy should be investigated to ensure data accuracy.</p>",
        entityLabel: "Vehicle: DL-01-AB-2237",
        entityId: "DL-01-AB-2237",
        assetCode: "DL-01-AB-2237",
        ownerCode: "FLEET-005",
        altName: "VEH-2237",
        location: "HYD",
        subLocation: "Hangar 1",
        assetType: "Commercial Vehicle",
        assetModel: "Commercial Vehicle",
        assetSubType: "Truck",
        site: "HYD",
        status: "New",
        assignee: "Unassigned",
        createdAt: moment().subtract(8, "hours"),
        dueAt: moment().add(5, "days"),
        analysisStarted: false,
        analysisComplete: false,
        statusHistory: []
      },
      {
        id: "AL-10316",
        severity: "Info",
        type: "Full Tank to Full Tank Mileage",
        title: "Calculated mileage is outside the defined threshold",
        body: "<p>The calculated mileage for vehicle <strong>COV-336 (AS-01-MC-1338 / DURO-336)</strong> shows a deviation from the standard consumption rate.</p><p><strong>Issue:</strong> The vehicle consumed fuel at a rate slightly higher than expected.</p><p><strong>Possible Causes:</strong></p><ul><li>Potential fuel loss</li><li>Inefficient driving patterns</li><li>Mechanical issues</li></ul><p><strong>Vehicle Location:</strong> DEL Service Bay</p><p><strong>Action Required:</strong> Should be monitored for further investigation.</p>",
        entityLabel: "Asset: COV-336 / AS-01-MC-1338 / DURO-336",
        entityId: "COV-336",
        assetCode: "COV-336",
        altName: "AS-01-MC-1338",
        ownerCode: "DURO-336",
        location: "DEL",
        subLocation: "Service Bay",
        assetType: "Commercial Vehicle",
        assetModel: "TATA DI",
        assetSubType: "Commercial Vehicle",
        site: "DEL",
        status: "New",
        assignee: "Unassigned",
        createdAt: moment().subtract(12, "hours"),
        dueAt: moment().add(10, "days"),
        analysisStarted: false,
        analysisComplete: false,
        statusHistory: []
      }
    ]
  };

  componentDidMount() {
    // Initialize default filter (alertType) with all values
    this.initializeDefaultFilters();
  }

  initializeDefaultFilters = () => {
    this.setState((s) => {
      const newEnabledFilterValues = { ...s.enabledFilterValues };
      
      // Initialize all active filter types with all their values
      s.activeFilterTypes.forEach((filterType) => {
        if (!newEnabledFilterValues[filterType] || !Array.isArray(newEnabledFilterValues[filterType]) || newEnabledFilterValues[filterType].length === 0) {
          const allValues = new Set();
          s.data.forEach((c) => {
            // Exclude Action Taken alerts
            if (c.status === "Action Taken") return;
            const val = this.getFilterValue(c, filterType);
            if (val) allValues.add(val);
          });
          newEnabledFilterValues[filterType] = Array.from(allValues);
        }
      });
      
      return { enabledFilterValues: newEnabledFilterValues };
    });
  };

  refresh = async () => {
    this.setState({ loading: true });
    setTimeout(() => {
      this.setState({ loading: false });
      message.success("Alerts refreshed");
    }, 600);
  };

  setFilter = (patch) => {
    this.setState((s) => ({ filters: { ...s.filters, ...patch } }));
  };

  clearFilters = () => {
    this.setState({
      filters: { q: "" },
      enabledFilterValues: {}
    });
  };

  handleFilterTypeChange = (filterType, isChecked) => {
    this.setState((s) => {
      const newActiveFilterTypes = isChecked
        ? [...s.activeFilterTypes, filterType].filter((v, i, arr) => arr.indexOf(v) === i) // Add and dedupe
        : s.activeFilterTypes.filter(ft => ft !== filterType); // Remove
      
      const newEnabledFilterValues = { ...s.enabledFilterValues };
      
      if (isChecked) {
        // Get all unique values for this filter type and enable all by default
        const allValues = new Set();
        s.data.forEach((c) => {
          // Exclude Action Taken alerts
          if (c.status === "Action Taken") return;
          const val = this.getFilterValue(c, filterType);
          if (val) allValues.add(val);
        });
        
        // Enable all values by default when filter type is activated
        newEnabledFilterValues[filterType] = Array.from(allValues);
      } else {
        // Remove enabled values for this filter type
        delete newEnabledFilterValues[filterType];
      }
      
      return {
        activeFilterTypes: newActiveFilterTypes,
        enabledFilterValues: newEnabledFilterValues
      };
    });
  };

  toggleFilterValue = (filterType, value) => {
    this.setState((s) => {
      const newEnabledFilterValues = { ...s.enabledFilterValues };
      
      // Get or create enabled values array for this filter type
      if (!newEnabledFilterValues[filterType]) {
        newEnabledFilterValues[filterType] = [];
      }
      
      const enabledValues = new Set(newEnabledFilterValues[filterType]);
      
      if (enabledValues.has(value)) {
        enabledValues.delete(value);
      } else {
        enabledValues.add(value);
      }
      
      // Get all unique values for this filter type
      const allValues = new Set();
      s.data.forEach((c) => {
        const val = this.getFilterValue(c, filterType);
        if (val) allValues.add(val);
      });
      
      // If all are disabled, enable all (prevent empty state)
      if (enabledValues.size === 0) {
        allValues.forEach((v) => enabledValues.add(v));
      }
      
      newEnabledFilterValues[filterType] = Array.from(enabledValues);
      
      return { enabledFilterValues: newEnabledFilterValues };
    });
  };

  selectAllFilterValues = (filterType) => {
    this.setState((s) => {
      const newEnabledFilterValues = { ...s.enabledFilterValues };
      
      // Get all unique values for this filter type (excluding Action Taken alerts)
      const allValues = new Set();
      s.data.forEach((c) => {
        // Exclude Action Taken alerts
        if (c.status === "Action Taken") return;
        const val = this.getFilterValue(c, filterType);
        if (val) allValues.add(val);
      });
      
      // Enable all values
      newEnabledFilterValues[filterType] = Array.from(allValues);
      
      return { enabledFilterValues: newEnabledFilterValues };
    });
  };

  selectNoneFilterValues = (filterType) => {
    this.setState((s) => {
      const newEnabledFilterValues = { ...s.enabledFilterValues };
      
      // Disable all values - set to empty array (this will show no cards)
      newEnabledFilterValues[filterType] = [];
      
      return { enabledFilterValues: newEnabledFilterValues };
    });
  };

  getDynamicCount = (filterType, value) => {
    const { data, filters, activeFilterTypes, enabledFilterValues } = this.state;
    let count = 0;
    
    data.forEach((alert) => {
      // Exclude Action Taken alerts
      if (alert.status === "Action Taken") return;
      
      // Check if alert matches the value for this filter type
      const cardValue = this.getFilterValue(alert, filterType);
      if (cardValue !== value) {
        return; // Skip if doesn't match this value
      }
      
      // Check search filter
      const q = (filters.q || "").trim().toLowerCase();
      if (q) {
        const haystack = (
          alert.id +
          " " +
          alert.title +
          " " +
          (alert.entityId || "") +
          " " +
          (alert.location || "") +
          " " +
          (alert.assetCode || "")
        ).toLowerCase();
        if (!haystack.includes(q)) {
          return; // Skip if doesn't match search
        }
      }
      
      // Check all OTHER active filter types (excluding current filterType)
      // Apply filters in the order they appear in activeFilterTypes (priority based on order)
      let matchesOtherFilters = true;
      for (const otherFilterType of activeFilterTypes) {
        if (otherFilterType === filterType) {
          continue; // Skip the current filter type
        }
        
        const cardOtherValue = this.getFilterValue(alert, otherFilterType);
        
        if (!enabledFilterValues[otherFilterType] || !Array.isArray(enabledFilterValues[otherFilterType])) {
          continue;
        }
        
        const enabledOtherValues = new Set(enabledFilterValues[otherFilterType]);
        
        // Get all unique values for this other filter type from the original data
        const allOtherValues = new Set();
        data.forEach((c) => {
          // Exclude Action Taken alerts when calculating all values
          if (c.status === "Action Taken") return;
          const val = this.getFilterValue(c, otherFilterType);
          if (val) allOtherValues.add(val);
        });
        
        // Filter logic:
        // - If no values are enabled (size === 0), filter out all (matchesOtherFilters = false)
        // - If all values are enabled (size === allOtherValues.size), show all (no filtering)
        // - If some values are enabled (0 < size < allOtherValues.size), filter based on enabled values
        if (enabledOtherValues.size === 0) {
          matchesOtherFilters = false;
          break;
        } else if (enabledOtherValues.size < allOtherValues.size) {
          if (!enabledOtherValues.has(cardOtherValue)) {
            matchesOtherFilters = false;
            break;
          }
        }
        // If enabledOtherValues.size === allOtherValues.size, no filtering needed (show all)
      }
      
      if (matchesOtherFilters) {
        count++;
      }
    });
    
    return count;
  };

  getFilterTypeLabel = (filterType) => {
    const labelMap = {
      "location": "Location",
      "subLocation": "Sub Location",
      "assetModel": "Asset Model",
      "assetType": "Asset Type",
      "assetSubType": "Asset Sub Type",
      "alertType": "Alert Type",
      "severity": "Severity",
      "assignee": "Assignee"
    };
    return labelMap[filterType] || filterType;
  };

  renderDynamicFilterChips = () => {
    const { data, activeFilterTypes, enabledFilterValues } = this.state;
    
    if (activeFilterTypes.length === 0) {
      return null;
    }
    
    const filterSections = [];
    
    activeFilterTypes.forEach((filterType) => {
      // Get all unique values for this filter type
      const allValues = new Set();
      data.forEach((c) => {
        const value = this.getFilterValue(c, filterType);
        if (value) {
          allValues.add(value);
        }
      });
      
      // Sort by value name
      const sortedValues = Array.from(allValues).sort((a, b) => a.localeCompare(b));
      
      // Initialize enabled values for this filter type if not exists
      if (!enabledFilterValues[filterType] || !Array.isArray(enabledFilterValues[filterType])) {
        const newEnabledFilterValues = { ...enabledFilterValues };
        newEnabledFilterValues[filterType] = sortedValues;
        this.setState({ enabledFilterValues: newEnabledFilterValues });
        return; // Will re-render with initialized values
      }
      
      const enabledValues = new Set(enabledFilterValues[filterType]);
      
      // Don't auto-enable if array is empty - respect "Select None" action
      // Empty array means user explicitly unselected all, so show no cards
      
      // Create chips for this filter type
      const chips = [];
      let hasVisibleChips = false;
      
      sortedValues.forEach((value) => {
        // Calculate dynamic count based on other filters
        const dynamicCount = this.getDynamicCount(filterType, value);
        
        // Skip chips with 0 count - they should not be displayed
        if (dynamicCount === 0) {
          return;
        }
        
        hasVisibleChips = true;
        
        const isActive = enabledValues.has(value);
        
        // Get color for alert type if this is the alertType filter
        let chipBgColor = isActive ? "rgba(255, 255, 255, 0.9)" : "#e9ebf0";
        let chipTextColor = isActive ? "#172b4d" : "#42526e";
        
        if (filterType === "alertType") {
          const typeColor = getAlertTypeColor(value);
          if (isActive) {
            chipBgColor = typeColor.bg;
            chipTextColor = typeColor.color;
          }
        }
        
        chips.push(
          <button
            key={value}
            onClick={() => this.toggleFilterValue(filterType, value)}
            style={{
              fontSize: 12,
              padding: "6px 10px",
              borderRadius: 999,
              border: "none",
              background: chipBgColor,
              color: chipTextColor,
              cursor: "pointer",
              whiteSpace: "nowrap",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              transition: "all 0.15s ease",
              boxShadow: "0 1px 3px rgba(9, 30, 66, 0.15)",
              fontWeight: filterType === "alertType" ? 600 : "normal"
            }}
            onMouseEnter={(e) => {
              if (!isActive) {
                e.target.style.transform = "translateY(-1px)";
                e.target.style.boxShadow = "0 2px 4px rgba(9, 30, 66, 0.2)";
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive) {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 1px 3px rgba(9, 30, 66, 0.15)";
              } else if (filterType === "alertType") {
                const typeColor = getAlertTypeColor(value);
                e.target.style.background = typeColor.bg;
                e.target.style.color = typeColor.color;
              }
            }}
          >
            <span>{value}</span>
            <span style={{ fontWeight: 600, fontSize: 12, color: chipTextColor }}>
              {dynamicCount}
            </span>
          </button>
        );
      });
      
      // Only add the section if there are visible chips
      if (hasVisibleChips) {
        filterSections.push(
          <div key={filterType} style={{ marginBottom: 8 }}>
            <div style={{ 
              fontSize: 10, 
              textTransform: "uppercase", 
              letterSpacing: "0.04em", 
              color: "rgba(0,0,0,0.45)", 
              marginBottom: 4,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 8
            }}>
              <span>{this.getFilterTypeLabel(filterType)}</span>
              <div style={{ display: "flex", gap: 4 }}>
                <button
                  onClick={() => this.selectAllFilterValues(filterType)}
                  style={{
                    fontSize: 10,
                    padding: "2px 6px",
                    borderRadius: 4,
                    border: "none",
                    background: "transparent",
                    color: "rgba(0,0,0,0.45)",
                    cursor: "pointer",
                    transition: "all 0.15s ease"
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "#f4f5f7";
                    e.target.style.color = "#172b4d";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "transparent";
                    e.target.style.color = "rgba(0,0,0,0.45)";
                  }}
                >
                  Select All
                </button>
                <button
                  onClick={() => this.selectNoneFilterValues(filterType)}
                  style={{
                    fontSize: 10,
                    padding: "2px 6px",
                    borderRadius: 4,
                    border: "none",
                    background: "transparent",
                    color: "rgba(0,0,0,0.45)",
                    cursor: "pointer",
                    transition: "all 0.15s ease"
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "#f4f5f7";
                    e.target.style.color = "#172b4d";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "transparent";
                    e.target.style.color = "rgba(0,0,0,0.45)";
                  }}
                >
                  Select None
                </button>
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, width: "100%", maxWidth: "100%" }}>
              {chips}
            </div>
          </div>
        );
      }
    });
    
    if (filterSections.length === 0) {
      return null;
    }
    
    return (
      <div style={{
        padding: "8px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        borderBottom: "1px solid #dfe1e6",
        background: "rgba(255, 255, 255, 0.5)",
        width: "100%",
        maxWidth: "100%"
      }}>
        <div style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          width: "100%",
          maxWidth: "1200px"
        }}>
          {filterSections}
        </div>
      </div>
    );
  };


  getCurrentUser = () => {
    try {
      const userData = getUserData();
      return userData?.email || userData?.name || userData?.username || "Current User";
    } catch (e) {
      return "Current User";
    }
  };

  getOrganizationName = () => {
    try {
      const userData = getUserData();
      if (userData?.organisation) {
        return typeof userData.organisation === 'string' 
          ? userData.organisation 
          : (userData.organisation.name || 'N/A');
      }
      return 'N/A';
    } catch (e) {
      return 'N/A';
    }
  };

  stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  generateWhatsAppMessage = (alert) => {
    const orgName = this.getOrganizationName();
    const alertDate = moment(alert.createdAt).format('DD MMM YYYY');
    const alertTime = moment(alert.createdAt).format('HH:mm');
    const alertDateTime = `${alertDate} ${alertTime}`;
    
    // Get asset model - combine asset type, sub type, and model if available
    const modelParts = [];
    if (alert.assetType) modelParts.push(alert.assetType);
    if (alert.assetSubType) modelParts.push(alert.assetSubType);
    if (alert.assetModel) modelParts.push(alert.assetModel);
    const model = modelParts.length > 0 ? modelParts.join(' ') : 'N/A';
    
    // Strip HTML from alert body
    const alertDetails = this.stripHtml(alert.body || alert.title || 'N/A');
    
    // Get analysis and action suggested from state or alert data
    const analysis = this.state.analysisText || alert.analysis || 'N/A';
    const actionSuggested = this.state.actionSuggestedText || alert.actionSuggested || 'N/A';
    
    const message = `*AIM Update:*
*Sl No*: ${alert.id}
*Date and time of Alert*: ${alertDateTime}
*Organisation Name*: ${orgName}
*Asset Code*: ${alert.assetCode || 'N/A'}${alert.altName ? ` ${alert.altName}` : ''}${alert.ownerCode ? ` ${alert.ownerCode}` : ''}
*Model*: ${model}
*Alert*: ${alertDetails}
*Analysis*: ${analysis}
*Suggested Action*: ${actionSuggested}`;

    return message;
  };

  copyWhatsAppMessage = (alert) => {
    const whatsappMessage = this.generateWhatsAppMessage(alert);
    
    // Create a temporary textarea element to copy text
    const textarea = document.createElement('textarea');
    textarea.value = whatsappMessage;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    
    try {
      document.execCommand('copy');
      message.success('WhatsApp message copied to clipboard!');
    } catch (err) {
      message.error('Failed to copy message. Please try again.');
      console.error('Failed to copy:', err);
    }
    
    document.body.removeChild(textarea);
  };

  updateAlertStatus = (record, newStatus, action, actionDetails = {}) => {
    const now = moment();
    const previousHistory = record.statusHistory || [];
    const currentUser = this.getCurrentUser();
    
    // Calculate duration in previous status
    let durationInPreviousStatus = null;
    if (previousHistory.length > 0) {
      const lastChange = previousHistory[previousHistory.length - 1];
      const duration = now.diff(moment(lastChange.timestamp), 'minutes');
      durationInPreviousStatus = {
        minutes: duration,
        hours: Math.floor(duration / 60),
        days: Math.floor(duration / (60 * 24)),
        humanReadable: this.formatDuration(duration)
      };
    } else if (record.createdAt) {
      // First status change - calculate from creation time
      const duration = now.diff(moment(record.createdAt), 'minutes');
      durationInPreviousStatus = {
        minutes: duration,
        hours: Math.floor(duration / 60),
        days: Math.floor(duration / (60 * 24)),
        humanReadable: this.formatDuration(duration)
      };
    }

    const statusChange = {
      from: record.status,
      to: newStatus,
      action: action,
      timestamp: now,
      timestampFormatted: now.format("DD MMM YYYY, HH:mm:ss"),
      userId: currentUser,
      userName: currentUser,
      durationInPreviousStatus: durationInPreviousStatus,
      ...actionDetails
    };

    this.setState((s) => {
      if (!s.data) {
        return {};
      }
      return {
        data: s.data.map((r) => {
          if (!r || r.id !== record.id) return r;
          return {
            ...r,
            status: newStatus,
            statusHistory: [...(r.statusHistory || []), statusChange],
            analysisStarted: newStatus === "Under Analysis" ? true : (r.analysisStarted || false),
            analysisComplete: newStatus === "Action Pending" ? true : (r.analysisComplete || false),
            lastStatusChangeTime: now
          };
        }),
        selectedAlert: s.selectedAlert && s.selectedAlert.id === record.id 
          ? {
              ...s.selectedAlert,
              status: newStatus,
              statusHistory: [...(s.selectedAlert.statusHistory || []), statusChange],
              analysisStarted: newStatus === "Under Analysis" ? true : (s.selectedAlert.analysisStarted || false),
              analysisComplete: newStatus === "Action Pending" ? true : (s.selectedAlert.analysisComplete || false),
              lastStatusChangeTime: now
            }
          : s.selectedAlert
      };
    });
  };

  formatDuration = (minutes) => {
    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    } else if (minutes < 1440) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      if (mins === 0) {
        return `${hours} hour${hours !== 1 ? 's' : ''}`;
      }
      return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''}`;
    } else {
      const days = Math.floor(minutes / 1440);
      const hours = Math.floor((minutes % 1440) / 60);
      if (hours === 0) {
        return `${days} day${days !== 1 ? 's' : ''}`;
      }
      return `${days} day${days !== 1 ? 's' : ''} ${hours} hour${hours !== 1 ? 's' : ''}`;
    }
  };

  getCurrentStatusDuration = (alert) => {
    if (!alert.statusHistory || alert.statusHistory.length === 0) {
      if (alert.createdAt) {
        const duration = moment().diff(moment(alert.createdAt), 'minutes');
        return this.formatDuration(duration);
      }
      return null;
    }
    const lastChange = alert.statusHistory[alert.statusHistory.length - 1];
    const duration = moment().diff(moment(lastChange.timestamp), 'minutes');
    return this.formatDuration(duration);
  };

  getStatusDurations = (alert) => {
    const durations = {};
    const history = alert.statusHistory || [];
    
    if (history.length === 0) {
      if (alert.createdAt) {
        const duration = moment().diff(moment(alert.createdAt), 'minutes');
        durations[alert.status] = {
          duration: duration,
          humanReadable: this.formatDuration(duration),
          startTime: alert.createdAt
        };
      }
      return durations;
    }

    // Calculate duration for each status
    let previousTime = moment(alert.createdAt);
    history.forEach((change, index) => {
      const statusStart = previousTime;
      const statusEnd = moment(change.timestamp);
      const duration = statusEnd.diff(statusStart, 'minutes');
      
      if (!durations[change.from]) {
        durations[change.from] = {
          duration: duration,
          humanReadable: this.formatDuration(duration),
          startTime: statusStart,
          endTime: statusEnd
        };
      } else {
        // If status was visited multiple times, accumulate duration
        durations[change.from].duration += duration;
        durations[change.from].humanReadable = this.formatDuration(durations[change.from].duration);
      }
      
      previousTime = statusEnd;
    });

    // Calculate duration for current status
    const lastChange = history[history.length - 1];
    const currentDuration = moment().diff(moment(lastChange.timestamp), 'minutes');
    if (!durations[alert.status]) {
      durations[alert.status] = {
        duration: currentDuration,
        humanReadable: this.formatDuration(currentDuration),
        startTime: moment(lastChange.timestamp),
        endTime: null // Still ongoing
      };
    } else {
      durations[alert.status].duration += currentDuration;
      durations[alert.status].humanReadable = this.formatDuration(durations[alert.status].duration);
    }

    return durations;
  };

  startAnalysis = (record, analysis = "", actionSuggested = "") => {
    this.updateAlertStatus(record, "Under Analysis", "Start Analysis", {
      analysis: analysis,
      actionSuggested: actionSuggested
    });
    // Update alert data with analysis fields
    this.setState((s) => ({
      data: s.data.map((r) => {
        if (r.id !== record.id) return r;
        return { ...r, analysis, actionSuggested };
      }),
      selectedAlert: s.selectedAlert && s.selectedAlert.id === record.id 
        ? { ...s.selectedAlert, analysis, actionSuggested }
        : s.selectedAlert
    }));
    message.success("Analysis started");
  };

  completeAnalysis = (record, analysis = "", actionSuggested = "") => {
    this.updateAlertStatus(record, "Action Pending", "Complete Analysis", {
      analysis: analysis,
      actionSuggested: actionSuggested
    });
    // Update alert data with analysis fields
    this.setState((s) => ({
      data: s.data.map((r) => {
        if (r.id !== record.id) return r;
        return { ...r, analysis, actionSuggested };
      }),
      selectedAlert: s.selectedAlert && s.selectedAlert.id === record.id 
        ? { ...s.selectedAlert, analysis, actionSuggested }
        : s.selectedAlert
    }));
    message.success("Analysis complete - Action pending");
  };

  markActionTaken = (record, actionType, actionDetails = {}) => {
    this.updateAlertStatus(record, "Action Taken", actionType, actionDetails);
    message.success(`Alert marked as Action Taken: ${actionType}`);
    // Close drawer if this alert is currently selected, since it will be removed from the board
    if (this.state.selectedAlert && this.state.selectedAlert.id === record.id) {
      setTimeout(() => {
        this.closeDrawer();
      }, 1000); // Close after 1 second to show the success message
    }
  };

  applyRowAction = (record, action, actionDetails = {}) => {
    if (action === "Start Analysis") {
      this.startAnalysis(record);
      return;
    }
    if (action === "Complete Analysis") {
      this.completeAnalysis(record);
      return;
    }
    if (action === "Ignore") {
      this.markActionTaken(record, "Ignored", { ...actionDetails, actionTaken: "Ignored" });
      return;
    }
    if (action === "Take Action" || action === "Upload Document" || action === "Field Team Action") {
      this.markActionTaken(record, action, actionDetails);
      return;
    }
    message.success(`${action}: ${record.id}`);
  };

  openDrawer = (alert) => {
    // Initialize form fields from alert data if available
    this.setState({
      drawerVisible: true,
      selectedAlert: alert,
      analysisText: alert.analysis || "",
      actionSuggestedText: alert.actionSuggested || "",
      actionTakenText: alert.actionTaken || "",
      fileList: alert.attachments || []
    });
  };

  closeDrawer = () => {
    this.setState({
      drawerVisible: false,
      drawerExpanded: false,
      selectedAlert: null,
      analysisText: "",
      actionSuggestedText: "",
      actionTakenText: "",
      fileList: []
    });
  };

  toggleDrawerExpand = () => {
    this.setState((s) => ({
      drawerExpanded: !s.drawerExpanded
    }));
  };

  getFilterValue = (alert, filterType) => {
    const fieldMap = {
      "location": "location",
      "subLocation": "subLocation",
      "assetModel": "assetModel",
      "assetType": "assetType",
      "assetSubType": "assetSubType",
      "alertType": "type",
      "severity": "severity",
      "assignee": "assignee"
    };
    const fieldName = fieldMap[filterType];
    if (!fieldName) return "";
    const value = (alert[fieldName] || "").trim();
    // For assignee, return "Unassigned" if empty or "Unassigned"
    if (filterType === "assignee" && (!value || value === "Unassigned")) {
      return "Unassigned";
    }
    return value;
  };

  getFilteredData = () => {
    const { data, filters, activeFilterTypes, enabledFilterValues } = this.state;
    return data.filter((r) => {
      // Exclude Action Taken alerts from board (they go to separate report)
      if (r.status === "Action Taken") return false;

      const q = (filters.q || "").trim().toLowerCase();
      const matchesQ =
        !q ||
        r.id.toLowerCase().includes(q) ||
        r.title.toLowerCase().includes(q) ||
        r.entityId.toLowerCase().includes(q) ||
        (r.location || "").toLowerCase().includes(q) ||
        (r.assetCode || "").toLowerCase().includes(q);

      if (!matchesQ) {
        return false;
      }

      // Check dynamic filter types in order (priority based on order of filters being enabled)
      // Apply filters sequentially - each filter narrows down the results for the next filter
      for (const filterType of activeFilterTypes) {
        const cardValue = this.getFilterValue(r, filterType);
        
        if (!enabledFilterValues[filterType] || !Array.isArray(enabledFilterValues[filterType])) {
          continue; // Skip if no enabled values set
        }
        
        const enabledValues = new Set(enabledFilterValues[filterType]);
        
        // Get all unique values for this filter type from the original data
        const allValues = new Set();
        data.forEach((c) => {
          // Exclude Action Taken alerts when calculating all values
          if (c.status === "Action Taken") return;
          const val = this.getFilterValue(c, filterType);
          if (val) allValues.add(val);
        });
        
        // Filter logic:
        // - If no values are enabled (size === 0), show nothing (filter out all)
        // - If all values are enabled (size === allValues.size), show all (no filtering)
        // - If some values are enabled (0 < size < allValues.size), filter based on enabled values
        if (enabledValues.size === 0) {
          return false; // No values selected, filter out all alerts
        } else if (enabledValues.size < allValues.size) {
          // Some values are selected, filter based on enabled values
          if (!enabledValues.has(cardValue)) {
            return false; // Alert doesn't match this filter
          }
        }
        // If enabledValues.size === allValues.size, no filtering needed (show all)
      }

      return true;
    });
  };

  getKpis = (rows) => {
    const open = rows.length;
    const criticalOpen = rows.filter((r) => r.severity === "Critical").length;
    const overdue = rows.filter((r) => isOverdue(r)).length;
    const newToday = rows.filter((r) => moment(r.createdAt).isSame(moment(), "day")).length;
    return { open, criticalOpen, overdue, newToday };
  };


  toggleColumnExpand = (columnId) => {
    this.setState((s) => ({
      expandedColumn: s.expandedColumn === columnId ? null : columnId
    }));
  };

  getCardsForColumn = (status) => {
    const rows = this.getFilteredData();
    const filtered = rows.filter((r) => r.status === status);
    // Sort by creation date and time in descending order (newest first)
    return filtered.sort((a, b) => {
      const dateA = moment(a.createdAt);
      const dateB = moment(b.createdAt);
      return dateB.valueOf() - dateA.valueOf(); // Descending order
    });
  };

  renderAlertCard = (r) => {
    const overdue = isOverdue(r);
    const dueStatus = getDueStatus(r);
    const daysSinceCreation = moment().diff(moment(r.createdAt), "days");

    const getSeverityBorderColor = (sev) => {
      if (sev === "Critical") return "#ff5630";
      if (sev === "Warning") return "#ffab00";
      return "#0065ff";
    };

    const getSeverityBgColor = (sev) => {
      if (sev === "Critical") return "#ff5630";
      if (sev === "Warning") return "#ffab00";
      return "#0065ff";
    };

    return (
      <div
        key={r.id}
        onClick={() => this.openDrawer(r)}
        style={{
          background: "#ffffff",
          borderRadius: "8px",
          padding: "8px 9px",
          marginBottom: 8,
          cursor: "pointer",
          boxShadow: "0 1px 3px rgba(9, 30, 66, 0.15)",
          border: "1px solid transparent",
          display: "flex",
          flexDirection: "column",
          gap: 4,
          transition: "transform 0.1s ease, box-shadow 0.1s ease, border-color 0.1s ease, background-color 0.1s ease"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = "#c1c7d0";
          e.currentTarget.style.transform = "translateY(-1px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = "transparent";
          e.currentTarget.style.transform = "translateY(0)";
        }}
      >
        {/* Top Row: Alert ID, Days Badge, Due Badge */}
        <div style={{ display: "flex", justifyContent: "space-between", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#172b4d" }}>{r.id}</span>
          <div style={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}>
            {/* Days Badge */}
            <span
              style={{
                fontSize: 10,
                padding: "2px 6px",
                borderRadius: 999,
                background: "#e3fcef",
                color: "#006644",
                whiteSpace: "nowrap",
                fontWeight: 600
              }}
            >
              {daysSinceCreation} {daysSinceCreation === 1 ? "day" : "days"}
            </span>
            {/* Due Badge */}
            {dueStatus && (
              <Tooltip title={moment(r.dueAt).format("DD MMM YYYY, HH:mm")}>
                <span
                  style={{
                    fontSize: 10,
                    padding: "2px 6px",
                    borderRadius: 999,
                    background: dueStatus.status === 'overdue' ? "#ffe5e5" : dueStatus.status === 'due' ? "#fff7e6" : "#e9f2ff",
                    color: dueStatus.status === 'overdue' ? "#cf1322" : dueStatus.status === 'due' ? "#faad14" : "#0747a6",
                    whiteSpace: "nowrap",
                    maxWidth: 130,
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                >
                  {dueStatus.status === 'overdue' && (
                    <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: "#ff5630", marginRight: 4, verticalAlign: "middle" }} />
                  )}
                  {dueStatus.status === 'overdue' 
                    ? `Overdue ${dueStatus.days}d`
                    : dueStatus.status === 'due'
                    ? "Due today"
                    : `Due in ${dueStatus.days}d`
                  }
                </span>
              </Tooltip>
            )}
          </div>
        </div>

        {/* Card Title */}
        <div style={{ fontSize: 12, color: "#172b4d", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {r.title}
        </div>

        {/* Card Line 1: Asset Type / Asset Code */}
        <div style={{ fontSize: 11, color: "#6b778c", display: "flex", justifyContent: "space-between", gap: 4 }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>
            {(() => {
              const parts = [];
              if (r.assetType) parts.push(r.assetType);
              if (r.assetSubType) parts.push(r.assetSubType);
              if (r.assetModel) parts.push(r.assetModel);
              return parts.length > 0 ? parts.join(" / ") : "—";
            })()}
          </span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: "9ch", flexShrink: 0, textAlign: "right" }}>
            {r.assetCode || "—"}
          </span>
        </div>

        {/* Card Line 2: Location / Assignee */}
        <div style={{ fontSize: 11, color: "#6b778c", display: "flex", justifyContent: "space-between", gap: 4 }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {[r.location, r.subLocation].filter(Boolean).join(" · ") || "—"}
          </span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {r.assignee === "Unassigned" ? (
              <span style={{ color: "#8c8c8c", fontStyle: "italic" }}>Unassigned</span>
            ) : (
              r.assignee || "Unassigned"
            )}
          </span>
        </div>

        {/* Card Tags */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 2 }}>
          <span
            style={{
              fontSize: 9,
              padding: "2px 6px",
              borderRadius: 999,
              background: getSeverityBgColor(r.severity),
              color: "#fff",
              fontWeight: "bold"
            }}
          >
            {r.severity.toUpperCase()}
          </span>
          {(() => {
            const typeColor = getAlertTypeColor(r.type);
            return (
              <span
                style={{
                  fontSize: 9,
                  padding: "2px 6px",
                  borderRadius: 999,
                  background: typeColor.bg,
                  color: typeColor.color,
                  fontWeight: "bold"
                }}
              >
                {r.type}
              </span>
            );
          })()}
        </div>

        {/* Card Footer */}
        <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 4, fontSize: 10, color: "#6b778c" }}>
          <div style={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {moment(r.createdAt).format("DD-MM-YYYY HH:mm")}
            </span>
          </div>
          {r.dueAt && (
            <div style={{ width: "100%", textAlign: "right" }}>
              <span style={{ fontWeight: "bold" }}>
                Due: {moment(r.dueAt).format("DD-MM-YYYY HH:mm")}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  render() {
    const { loading, filters, expandedColumn, drawerVisible, drawerExpanded, selectedAlert } = this.state;
    const allRows = this.getFilteredData();
    const kpis = this.getKpis(allRows);


    const totalAlerts = this.state.data.filter(r => r.status !== "Action Taken").length;

    return (
      <div style={{ background: "radial-gradient(circle at top left, #dde7ff 0, #f4f5f7 45%, #ffffff 100%)", minHeight: "100vh", height: "100vh", display: "flex", flexDirection: "column" }}>
        <Content style={{ padding: 0, flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* Title Section */}
          <div
            style={{
              padding: "16px 20px",
              borderBottom: "1px solid #dfe1e6",
              background: "rgba(255, 255, 255, 0.9)",
              position: "sticky",
              top: 0,
              zIndex: 10,
              backdropFilter: "blur(8px)"
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Icon type="inbox" style={{ fontSize: 20, color: "#1890ff" }} />
              <span style={{ fontSize: 20, fontWeight: 600, color: "#172b4d" }}>
                Alert Inbox
              </span>
              <Badge 
                count={totalAlerts} 
                style={{ 
                  backgroundColor: "#1890ff",
                  fontSize: 11,
                  fontWeight: 600
                }}
              />
            </div>
          </div>

          {/* Top Bar */}
          <div
            style={{
              padding: "12px 20px",
              display: "flex",
              alignItems: "center",
              gap: 16,
              borderBottom: "1px solid #dfe1e6",
              background: "rgba(255, 255, 255, 0.9)",
              position: "sticky",
              top: 0,
              zIndex: 10,
              backdropFilter: "blur(8px)"
            }}
          >
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", width: "100%" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, color: "rgba(0,0,0,0.45)", marginRight: 4 }}>Filter by:</span>
                <Checkbox
                  checked={this.state.activeFilterTypes.includes("alertType")}
                  onChange={(e) => this.handleFilterTypeChange("alertType", e.target.checked)}
                  style={{ fontSize: 11 }}
                >
                  Alert Type
                </Checkbox>
                <Checkbox
                  checked={this.state.activeFilterTypes.includes("location")}
                  onChange={(e) => this.handleFilterTypeChange("location", e.target.checked)}
                  style={{ fontSize: 11 }}
                >
                  Location
                </Checkbox>
                <Checkbox
                  checked={this.state.activeFilterTypes.includes("subLocation")}
                  onChange={(e) => this.handleFilterTypeChange("subLocation", e.target.checked)}
                  style={{ fontSize: 11 }}
                >
                  Sub Location
                </Checkbox>
                <Checkbox
                  checked={this.state.activeFilterTypes.includes("assetModel")}
                  onChange={(e) => this.handleFilterTypeChange("assetModel", e.target.checked)}
                  style={{ fontSize: 11 }}
                >
                  Asset Model
                </Checkbox>
                <Checkbox
                  checked={this.state.activeFilterTypes.includes("assetType")}
                  onChange={(e) => this.handleFilterTypeChange("assetType", e.target.checked)}
                  style={{ fontSize: 11 }}
                >
                  Asset Type
                </Checkbox>
                <Checkbox
                  checked={this.state.activeFilterTypes.includes("assetSubType")}
                  onChange={(e) => this.handleFilterTypeChange("assetSubType", e.target.checked)}
                  style={{ fontSize: 11 }}
                >
                  Asset Sub Type
                </Checkbox>
                <Checkbox
                  checked={this.state.activeFilterTypes.includes("severity")}
                  onChange={(e) => this.handleFilterTypeChange("severity", e.target.checked)}
                  style={{ fontSize: 11 }}
                >
                  Severity
                </Checkbox>
                <Checkbox
                  checked={this.state.activeFilterTypes.includes("assignee")}
                  onChange={(e) => this.handleFilterTypeChange("assignee", e.target.checked)}
                  style={{ fontSize: 11 }}
                >
                  Assignee
                </Checkbox>
              </div>
              <Input
                value={filters.q}
                onChange={(e) => this.setFilter({ q: e.target.value })}
                prefix={<Icon type="search" />}
                placeholder="Search alerts / asset / location..."
                allowClear
                style={{ width: 200 }}
              />
              <Button size="small" onClick={this.clearFilters}>
                Reset
              </Button>
              <Button 
                icon="reload" 
                onClick={this.refresh} 
                loading={loading}
                shape="circle"
                style={{ marginLeft: "auto" }}
              />
            </div>
          </div>

          {/* Dynamic Filter Chips Row */}
          {this.renderDynamicFilterChips()}




          {/* Alert Details Drawer */}
          <Drawer
            title={
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: 16 }}>
                      {selectedAlert?.id || "Alert Details"}
                    </span>
                    {selectedAlert && severityTag(selectedAlert.severity)}
                  </div>
                  {selectedAlert && (() => {
                    const typeColor = getAlertTypeColor(selectedAlert.type);
                    return (
                      <span
                        style={{
                          fontSize: 11,
                          padding: "2px 8px",
                          borderRadius: 999,
                          background: typeColor.bg,
                          color: typeColor.color,
                          fontWeight: 600
                        }}
                      >
                        {selectedAlert.type}
                      </span>
                    );
                  })()}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
                  <Button
                    type="text"
                    onClick={this.toggleDrawerExpand}
                    style={{ 
                      width: 40, 
                      height: 40, 
                      padding: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 16,
                      border: "none",
                      boxShadow: "none"
                    }}
                    title={drawerExpanded ? "Collapse" : "Expand to full width"}
                  >
                    <Icon type={drawerExpanded ? "fullscreen-exit" : "fullscreen"} />
                  </Button>
                  <Button
                    type="text"
                    icon="close"
                    onClick={this.closeDrawer}
                    style={{ 
                      width: 40, 
                      height: 40, 
                      padding: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 16,
                      border: "none",
                      boxShadow: "none"
                    }}
                    title="Close"
                  />
                </div>
              </div>
            }
            placement="right"
            closable={false}
            onClose={this.closeDrawer}
            visible={drawerVisible}
            width={drawerExpanded ? "100%" : 600}
            bodyStyle={{ 
              padding: drawerExpanded ? 24 : 24,
              borderLeft: drawerExpanded ? "1px solid #e8e8e8" : "none"
            }}
            style={drawerExpanded ? { maxWidth: "100vw" } : {}}
          >
            {selectedAlert && (
              <div style={{ paddingLeft: drawerExpanded ? 0 : 0 }}>
                  {/* Compact Alert Details at Top */}
                  <div style={{ 
                    marginBottom: 20, 
                    padding: "12px", 
                    background: "#fafafa", 
                    borderRadius: 6,
                    fontSize: 11
                  }}>
                    <Descriptions column={2} bordered={false} size="small" style={{ marginBottom: 0 }}>
                      <Descriptions.Item label="Alert ID">{selectedAlert.id}</Descriptions.Item>
                      <Descriptions.Item label="Status">
                        <Badge 
                          status={
                            selectedAlert.status === "New" ? "processing" :
                            selectedAlert.status === "Under Analysis" ? "warning" :
                            selectedAlert.status === "Action Pending" ? "error" : "default"
                          } 
                          text={selectedAlert.status} 
                        />
                      </Descriptions.Item>
                      <Descriptions.Item label="Severity">
                        <span
                          style={{
                            fontSize: 9,
                            padding: "2px 6px",
                            borderRadius: 999,
                            background: (() => {
                              if (selectedAlert.severity === "Critical") return "#ff5630";
                              if (selectedAlert.severity === "Warning") return "#ffab00";
                              return "#0065ff";
                            })(),
                            color: "#fff",
                            fontWeight: "bold"
                          }}
                        >
                          {selectedAlert.severity.toUpperCase()}
                        </span>
                      </Descriptions.Item>
                      <Descriptions.Item label="Type">
                        {(() => {
                          const typeColor = getAlertTypeColor(selectedAlert.type);
                          return (
                            <span
                              style={{
                                fontSize: 11,
                                padding: "2px 8px",
                                borderRadius: 999,
                                background: typeColor.bg,
                                color: typeColor.color,
                                fontWeight: 600
                              }}
                            >
                              {selectedAlert.type}
                            </span>
                          );
                        })()}
                      </Descriptions.Item>
                      <Descriptions.Item label="Asset Type / Sub Type / Model">
                        {(() => {
                          const parts = [];
                          if (selectedAlert.assetType) parts.push(selectedAlert.assetType);
                          if (selectedAlert.assetSubType) parts.push(selectedAlert.assetSubType);
                          if (selectedAlert.assetModel) parts.push(selectedAlert.assetModel);
                          return parts.length > 0 ? parts.join(" / ") : "";
                        })()}
                      </Descriptions.Item>
                      <Descriptions.Item label="Asset Code / Owner Code / Alt Name">
                        {(() => {
                          const parts = [];
                          if (selectedAlert.assetCode) parts.push(selectedAlert.assetCode);
                          if (selectedAlert.ownerCode) parts.push(selectedAlert.ownerCode);
                          if (selectedAlert.altName) parts.push(selectedAlert.altName);
                          return parts.length > 0 ? parts.join(" / ") : "";
                        })()}
                      </Descriptions.Item>
                      <Descriptions.Item label="Location / Sub Location">
                        {(() => {
                          const parts = [];
                          if (selectedAlert.location) parts.push(selectedAlert.location);
                          if (selectedAlert.subLocation) parts.push(selectedAlert.subLocation);
                          return parts.length > 0 ? parts.join(" / ") : "";
                        })()}
                      </Descriptions.Item>
                      <Descriptions.Item label="Assignee">
                        {selectedAlert.assignee === "Unassigned" ? (
                          <Tag color="default">Unassigned</Tag>
                        ) : (
                          selectedAlert.assignee
                        )}
                      </Descriptions.Item>
                      <Descriptions.Item label="Alert Age" span={selectedAlert.dueAt ? 1 : 2}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <span
                            style={{
                              fontSize: 10,
                              padding: "2px 6px",
                              borderRadius: 999,
                              background: "#e3fcef",
                              color: "#006644",
                              whiteSpace: "nowrap",
                              fontWeight: 600,
                              display: "inline-block",
                              width: "fit-content"
                            }}
                          >
                            {(() => {
                              const daysSinceCreation = moment().diff(moment(selectedAlert.createdAt), "days");
                              return `${daysSinceCreation} ${daysSinceCreation === 1 ? "day" : "days"}`;
                            })()}
                          </span>
                          <div style={{ fontSize: 11, color: "#8c8c8c", lineHeight: 1.5 }}>
                            <span style={{ fontWeight: 600, color: "#262626" }}>Alert Age: </span>
                            <span style={{ fontWeight: 600, color: "#262626" }}>Created: </span>
                            {moment(selectedAlert.createdAt).format("DD MMM YYYY, HH:mm:ss")}
                          </div>
                        </div>
                      </Descriptions.Item>
                      {selectedAlert.dueAt && (
                        <Descriptions.Item label="Action Due Date">
                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <span
                              style={{
                                fontSize: 10,
                                padding: "2px 6px",
                                borderRadius: 999,
                                background: (() => {
                                  const dueStatus = getDueStatus(selectedAlert);
                                  return dueStatus.status === 'overdue' ? "#ffe5e5" : dueStatus.status === 'due' ? "#fff7e6" : "#e9f2ff";
                                })(),
                                color: (() => {
                                  const dueStatus = getDueStatus(selectedAlert);
                                  return dueStatus.status === 'overdue' ? "#cf1322" : dueStatus.status === 'due' ? "#faad14" : "#0747a6";
                                })(),
                                whiteSpace: "nowrap",
                                fontWeight: 600,
                                display: "inline-block",
                                width: "fit-content"
                              }}
                            >
                              {(() => {
                                const dueStatus = getDueStatus(selectedAlert);
                                if (dueStatus.status === 'overdue') {
                                  return (
                                    <>
                                      <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: "#ff5630", marginRight: 4, verticalAlign: "middle" }} />
                                      {`Overdue ${dueStatus.days}d`}
                                    </>
                                  );
                                } else if (dueStatus.status === 'due') {
                                  return "Due today";
                                } else {
                                  return `Due in ${dueStatus.days}d`;
                                }
                              })()}
                            </span>
                            <div style={{ fontSize: 11, color: "#8c8c8c", lineHeight: 1.5 }}>
                              <span style={{ fontWeight: 600, color: "#262626" }}>Action Due Date: </span>
                              {moment(selectedAlert.dueAt).format("DD MMM YYYY, HH:mm")}
                            </div>
                          </div>
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  </div>

                  {/* Title and Body - Prominent */}
                  <div style={{ marginBottom: 24 }}>
                    <Title level={3} style={{ marginBottom: 12, fontSize: 20, fontWeight: 600 }}>
                      {selectedAlert.title}
                    </Title>
                    {selectedAlert.body && (
                      <div 
                        style={{ 
                          marginBottom: 0, 
                          color: "#595959", 
                          fontSize: 15, 
                          lineHeight: 1.7,
                          wordWrap: "break-word"
                        }}
                      >
                        <style>{`
                          .alert-body-content p {
                            margin: 0 0 12px 0;
                          }
                          .alert-body-content p:last-child {
                            margin-bottom: 0;
                          }
                          .alert-body-content strong, .alert-body-content b {
                            font-weight: 600;
                            color: #262626;
                          }
                          .alert-body-content em, .alert-body-content i {
                            font-style: italic;
                          }
                          .alert-body-content ul, .alert-body-content ol {
                            margin: 12px 0;
                            padding-left: 24px;
                          }
                          .alert-body-content li {
                            margin: 6px 0;
                          }
                          .alert-body-content h1, .alert-body-content h2, .alert-body-content h3, 
                          .alert-body-content h4, .alert-body-content h5, .alert-body-content h6 {
                            margin: 16px 0 8px 0;
                            font-weight: 600;
                            color: #262626;
                          }
                          .alert-body-content h1 { font-size: 20px; }
                          .alert-body-content h2 { font-size: 18px; }
                          .alert-body-content h3 { font-size: 16px; }
                          .alert-body-content code {
                            background: #f5f5f5;
                            padding: 2px 6px;
                            border-radius: 3px;
                            font-family: 'Courier New', monospace;
                            font-size: 13px;
                          }
                          .alert-body-content a {
                            color: #1890ff;
                            text-decoration: none;
                          }
                          .alert-body-content a:hover {
                            text-decoration: underline;
                          }
                          .alert-body-content blockquote {
                            margin: 12px 0;
                            padding-left: 16px;
                            border-left: 3px solid #e8e8e8;
                            color: #8c8c8c;
                          }
                        `}</style>
                        <div 
                          className="alert-body-content"
                          dangerouslySetInnerHTML={{ __html: selectedAlert.body }}
                        />
                      </div>
                    )}
                  </div>
                    
                    {/* Link to open analysis data - Available for all alert types */}
                    <div style={{ marginBottom: 24 }}>
                      <Button
                        type="link"
                        icon="file-search"
                        onClick={() => {
                          // Open Alert Analysis in new window
                          const newWindow = window.open('', '_blank');
                          if (newWindow) {
                            newWindow.document.write(`
                              <html>
                                <head>
                                  <title>Alert Analysis - ${selectedAlert.id}</title>
                                  <style>
                                    body { margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; }
                                    .container { padding: 20px; }
                                  </style>
                                </head>
                                <body>
                                  <div class="container">
                                    <h2>Alert Analysis</h2>
                                    <p><strong>Alert ID:</strong> ${selectedAlert.id}</p>
                                    <p><strong>Alert Type:</strong> ${selectedAlert.type}</p>
                                    <p><strong>Asset:</strong> ${selectedAlert.assetCode || 'N/A'}</p>
                                    <p>This would open the full Alert Analysis UI in a new window.</p>
                                  </div>
                                </body>
                              </html>
                            `);
                          }
                        }}
                        style={{ padding: 0, height: "auto", lineHeight: "normal" }}
                      >
                        Open Analysis Data in New Window
                      </Button>
                    </div>

                  {/* Workflow Actions - Prominent */}
                  {selectedAlert.status !== "Action Taken" && (
                    <div style={{ marginTop: 24 }}>
                      <div style={{ 
                        fontSize: 14, 
                        fontWeight: 600, 
                        color: "#172b4d", 
                        marginBottom: 16,
                        paddingBottom: 8,
                        borderBottom: "1px solid #e8e8e8"
                      }}>
                        Workflow Actions
                      </div>
                      {/* Start Analysis Button - Only for New status */}
                      {selectedAlert.status === "New" && (
                        <div style={{ marginBottom: 16 }}>
                          <Button
                            type="primary"
                            size="large"
                            icon="edit"
                            onClick={() => {
                              this.startAnalysis(selectedAlert, this.state.analysisText, this.state.actionSuggestedText);
                            }}
                            block
                          >
                            Start Analysis
                          </Button>
                        </div>
                      )}

                      {/* Analysis and Action Suggested Fields - Visible when Under Analysis or Action Pending */}
                      {(selectedAlert.status === "Under Analysis" || selectedAlert.status === "Action Pending") && (
                        <>
                          <div style={{ marginBottom: 16 }}>
                            <div style={{ marginBottom: 8, fontWeight: 600 }}>Analysis</div>
                            <TextArea
                              rows={4}
                              value={this.state.analysisText || ""}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                // Update both state and alert data in one setState
                                this.setState((s) => {
                                  if (!s.data || !selectedAlert) {
                                    return { analysisText: newValue };
                                  }
                                  return {
                                    analysisText: newValue,
                                    data: s.data.map((r) => {
                                      if (r.id !== selectedAlert.id) return r;
                                      return { ...r, analysis: newValue };
                                    }),
                                    selectedAlert: s.selectedAlert && s.selectedAlert.id === selectedAlert.id
                                      ? { ...s.selectedAlert, analysis: newValue }
                                      : s.selectedAlert
                                  };
                                });
                              }}
                              placeholder="Enter your analysis here..."
                            />
                          </div>
                          <div style={{ marginBottom: 16 }}>
                            <div style={{ marginBottom: 8, fontWeight: 600 }}>Action Suggested</div>
                            <TextArea
                              rows={4}
                              value={this.state.actionSuggestedText || ""}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                // Update both state and alert data in one setState
                                this.setState((s) => {
                                  if (!s.data || !selectedAlert) {
                                    return { actionSuggestedText: newValue };
                                  }
                                  return {
                                    actionSuggestedText: newValue,
                                    data: s.data.map((r) => {
                                      if (r.id !== selectedAlert.id) return r;
                                      return { ...r, actionSuggested: newValue };
                                    }),
                                    selectedAlert: s.selectedAlert && s.selectedAlert.id === selectedAlert.id
                                      ? { ...s.selectedAlert, actionSuggested: newValue }
                                      : s.selectedAlert
                                  };
                                });
                              }}
                              placeholder="Enter suggested action here..."
                            />
                          </div>
                        </>
                      )}

                      {/* Complete Analysis Button - Only for Under Analysis */}
                      {selectedAlert.status === "Under Analysis" && (
                        <div style={{ marginBottom: 16 }}>
                          <Button
                            type="primary"
                            size="large"
                            icon="check"
                            onClick={() => {
                              this.completeAnalysis(selectedAlert, this.state.analysisText, this.state.actionSuggestedText);
                            }}
                            block
                          >
                            Complete Analysis
                          </Button>
                        </div>
                      )}

                      {/* Action Taken Field - Only for Action Pending */}
                      {selectedAlert.status === "Action Pending" && (
                        <>
                          <div style={{ marginBottom: 16 }}>
                            <div style={{ marginBottom: 8, fontWeight: 600 }}>Action Taken</div>
                            <TextArea
                              rows={4}
                              value={this.state.actionTakenText || ""}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                // Update both state and alert data in one setState
                                this.setState((s) => {
                                  if (!s.data || !selectedAlert) {
                                    return { actionTakenText: newValue };
                                  }
                                  return {
                                    actionTakenText: newValue,
                                    data: s.data.map((r) => {
                                      if (r.id !== selectedAlert.id) return r;
                                      return { ...r, actionTaken: newValue };
                                    }),
                                    selectedAlert: s.selectedAlert && s.selectedAlert.id === selectedAlert.id
                                      ? { ...s.selectedAlert, actionTaken: newValue }
                                      : s.selectedAlert
                                  };
                                });
                              }}
                              placeholder="Enter action taken here..."
                            />
                          </div>
                          <div style={{ marginBottom: 16 }}>
                            <Button
                              type="primary"
                              size="large"
                              icon="check-circle"
                              onClick={() => {
                                this.markActionTaken(selectedAlert, "Action Completed", { 
                                  actionTaken: this.state.actionTakenText 
                                });
                              }}
                              block
                            >
                              Complete Action
                            </Button>
                          </div>
                          {/* WhatsApp Message Button - Only for Action Pending (after analysis is complete) */}
                          <div style={{ marginBottom: 16 }}>
                            <Button
                              type="default"
                              size="large"
                              icon="message"
                              onClick={() => {
                                this.copyWhatsAppMessage(selectedAlert);
                              }}
                              block
                              style={{ 
                                background: "#25D366",
                                borderColor: "#25D366",
                                color: "#fff"
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = "#20BA5A";
                                e.currentTarget.style.borderColor = "#20BA5A";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "#25D366";
                                e.currentTarget.style.borderColor = "#25D366";
                              }}
                            >
                              Copy WhatsApp Message
                            </Button>
                          </div>
                        </>
                      )}

                      {/* Direct Action Section - Only visible for New status (before analysis) */}
                      {selectedAlert.status === "New" && (
                        <>
                          <Divider>Or</Divider>
                          <div style={{ marginBottom: 16 }}>
                            <div style={{ marginBottom: 8, fontWeight: 600 }}>Action Taken (Direct - Skip Analysis)</div>
                            <TextArea
                              rows={3}
                              value={this.state.actionTakenText || ""}
                              onChange={(e) => {
                                this.setState({ actionTakenText: e.target.value });
                              }}
                              placeholder="Enter action taken directly (without analysis)..."
                            />
                            <Button
                              type="default"
                              size="large"
                              icon="check-circle"
                              onClick={() => {
                                this.markActionTaken(selectedAlert, "Direct Action", { 
                                  actionTaken: this.state.actionTakenText,
                                  directAction: true
                                });
                              }}
                              block
                              style={{ marginTop: 8 }}
                            >
                              Complete Action Directly
                            </Button>
                          </div>
                        </>
                      )}

                      {/* Document/Image Upload Section - Available for all statuses */}
                      <Divider style={{ marginTop: 24, marginBottom: 16 }}>Attachments</Divider>
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ marginBottom: 8, fontWeight: 600 }}>Upload Documents or Images</div>
                        <Upload
                          multiple
                          fileList={this.state.fileList || []}
                          onChange={(info) => {
                            // Handle file upload
                            let fileList = [...info.fileList];
                            
                            // Limit to 10 files
                            fileList = fileList.slice(-10);
                            
                            // Read file and create preview
                            fileList = fileList.map((file) => {
                              if (file.response) {
                                // File uploaded successfully
                                file.url = file.response.url;
                              }
                              return file;
                            });
                            
                            this.setState({ fileList });
                            
                            // Save to alert data
                            this.setState((s) => {
                              if (!s.data || !selectedAlert) {
                                return {};
                              }
                              return {
                                data: s.data.map((r) => {
                                  if (r.id !== selectedAlert.id) return r;
                                  return { ...r, attachments: fileList };
                                }),
                                selectedAlert: s.selectedAlert && s.selectedAlert.id === selectedAlert.id
                                  ? { ...s.selectedAlert, attachments: fileList }
                                  : s.selectedAlert
                              };
                            });
                          }}
                          onRemove={(file) => {
                            // Remove file from list
                            const fileList = (this.state.fileList || []).filter(item => item.uid !== file.uid);
                            this.setState({ fileList });
                            
                            // Update alert data
                            this.setState((s) => {
                              if (!s.data || !selectedAlert) {
                                return {};
                              }
                              return {
                                data: s.data.map((r) => {
                                  if (r.id !== selectedAlert.id) return r;
                                  return { ...r, attachments: fileList };
                                }),
                                selectedAlert: s.selectedAlert && s.selectedAlert.id === selectedAlert.id
                                  ? { ...s.selectedAlert, attachments: fileList }
                                  : s.selectedAlert
                              };
                            });
                          }}
                          beforeUpload={(file) => {
                            // Validate file type
                            const isImage = file.type.startsWith('image/');
                            const isDocument = file.type.includes('pdf') || 
                                              file.type.includes('document') || 
                                              file.type.includes('text') ||
                                              file.type.includes('spreadsheet') ||
                                              file.type.includes('presentation') ||
                                              file.name.endsWith('.doc') ||
                                              file.name.endsWith('.docx') ||
                                              file.name.endsWith('.xls') ||
                                              file.name.endsWith('.xlsx') ||
                                              file.name.endsWith('.ppt') ||
                                              file.name.endsWith('.pptx') ||
                                              file.name.endsWith('.txt') ||
                                              file.name.endsWith('.csv');
                            
                            if (!isImage && !isDocument) {
                              message.error('You can only upload images or documents!');
                              return false;
                            }
                            
                            // Validate file size (max 10MB)
                            const isLt10M = file.size / 1024 / 1024 < 10;
                            if (!isLt10M) {
                              message.error('File must be smaller than 10MB!');
                              return false;
                            }
                            
                            // For now, we'll just add it to the list
                            // In production, you would upload to a server here
                            const fileList = [...(this.state.fileList || []), file];
                            this.setState({ fileList });
                            
                            // Save to alert data
                            this.setState((s) => {
                              if (!s.data || !selectedAlert) {
                                return {};
                              }
                              return {
                                data: s.data.map((r) => {
                                  if (r.id !== selectedAlert.id) return r;
                                  return { ...r, attachments: fileList };
                                }),
                                selectedAlert: s.selectedAlert && s.selectedAlert.id === selectedAlert.id
                                  ? { ...s.selectedAlert, attachments: fileList }
                                  : s.selectedAlert
                              };
                            });
                            
                            // Return false to prevent auto upload
                            // In production, you would return true and handle upload via customRequest
                            return false;
                          }}
                          listType="picture-card"
                          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                        >
                          {(this.state.fileList || []).length < 10 && (
                            <div>
                              <Icon type="plus" />
                              <div className="ant-upload-text">Upload</div>
                            </div>
                          )}
                        </Upload>
                        <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 8 }}>
                          Supported formats: Images (JPG, PNG, GIF, etc.), PDF, Word, Excel, PowerPoint, Text files. Max file size: 10MB. Max 10 files.
                        </div>
                      </div>

                      {/* Ignore Button - Always visible */}
                      <Button
                        type="default"
                        size="large"
                        icon="stop"
                        onClick={() => {
                          this.applyRowAction(selectedAlert, "Ignore", { reason: "User ignored" });
                        }}
                        block
                        danger
                      >
                        Ignore Alert
                      </Button>
                    </div>
                  )}

                  {/* Status History */}
                  {selectedAlert.statusHistory && selectedAlert.statusHistory.length > 0 && (
                    <div style={{ marginTop: 24 }}>
                      <div style={{ 
                        fontSize: 14, 
                        fontWeight: 600, 
                        color: "#172b4d", 
                        marginBottom: 16,
                        paddingBottom: 8,
                        borderBottom: "1px solid #e8e8e8"
                      }}>
                        Status Change History
                      </div>
                      <div style={{ fontSize: 12 }}>
                        {selectedAlert.statusHistory.map((change, idx) => (
                          <div key={idx} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: idx < selectedAlert.statusHistory.length - 1 ? "1px solid #f0f0f0" : "none" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                              <span style={{ fontWeight: 600, fontSize: 13 }}>
                                {change.from} → {change.to}
                              </span>
                              <span style={{ color: "#8c8c8c", fontSize: 11 }}>
                                {change.timestampFormatted || moment(change.timestamp).format("DD MMM YYYY, HH:mm:ss")}
                              </span>
                            </div>
                            <div style={{ color: "#595959", marginBottom: 4 }}>
                              Action: <strong>{change.action}</strong>
                              {change.userId && (
                                <span style={{ color: "#8c8c8c", marginLeft: 8 }}>by {change.userId}</span>
                              )}
                            </div>
                            {change.durationInPreviousStatus && (
                              <div style={{ 
                                background: "#f0f2f5", 
                                padding: "6px 8px", 
                                borderRadius: 4, 
                                marginTop: 4,
                                fontSize: 11
                              }}>
                                <Icon type="clock-circle" style={{ marginRight: 4, color: "#1890ff" }} />
                                <Text strong style={{ color: "#1890ff" }}>
                                  Duration in "{change.from}": {change.durationInPreviousStatus.humanReadable}
                                </Text>
                              </div>
                            )}
                            {change.reason && (
                              <div style={{ color: "#8c8c8c", fontSize: 11, marginTop: 4 }}>
                                Reason: {change.reason}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
            )}
          </Drawer>

          {/* Kanban Board */}
          {loading ? (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <Icon type="loading" style={{ fontSize: 32 }} spin />
            </div>
          ) : allRows.length > 0 ? (
            <div style={{ padding: "12px 20px 20px", overflowX: "auto", flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
              <div
                style={{
                  display: "flex",
                  gap: 14,
                  width: "100%",
                  alignItems: "flex-start",
                  flex: 1,
                  minHeight: 0
                }}
              >
                {STATUS_COLUMNS.map((column) => {
                  const cards = this.getCardsForColumn(column.id);
                  const isExpanded = expandedColumn === column.id;
                  const hasExpanded = expandedColumn !== null;

                  return (
                    <div
                      key={column.id}
                      style={{
                        background: "rgba(255, 255, 255, 0.95)",
                        borderRadius: 10,
                        padding: 10,
                        width: isExpanded ? "100%" : hasExpanded ? 0 : 320,
                        minWidth: isExpanded ? "100%" : hasExpanded ? 0 : 320,
                        maxWidth: isExpanded ? "none" : hasExpanded ? 0 : 320,
                        height: isExpanded ? "100%" : "auto",
                        maxHeight: isExpanded ? "100%" : "calc(100vh - 300px)",
                        display: hasExpanded && !isExpanded ? "none" : "flex",
                        flexDirection: "column",
                        boxShadow: "0 1px 3px rgba(9, 30, 66, 0.15)",
                        flex: isExpanded ? "1 1 100%" : "none"
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                          {column.label}
                          <span style={{ fontSize: 11, padding: "2px 6px", borderRadius: 999, background: "#f4f5f7", color: "#6b778c" }}>
                            {cards.length}
                          </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          {column.subtitle && <span style={{ fontSize: 11, color: "#6b778c" }}>{column.subtitle}</span>}
                          <Button
                            size="small"
                            onClick={() => this.toggleColumnExpand(column.id)}
                            style={{ fontSize: 11, padding: "4px 8px" }}
                            title={isExpanded ? "Collapse" : "Expand"}
                          >
                            <Icon type={isExpanded ? "fullscreen-exit" : "fullscreen"} />
                          </Button>
                        </div>
                      </div>
                      <div
                        style={{
                          overflowY: "auto",
                          paddingRight: 4,
                          display: "flex",
                          flexDirection: isExpanded ? "row" : "column",
                          gap: 8,
                          minHeight: 40,
                          flexWrap: isExpanded ? "wrap" : "nowrap",
                          flex: 1,
                          height: isExpanded ? "100%" : "auto"
                        }}
                      >
                        {cards.length > 0 ? (
                          cards.map((card) => (
                            <div key={card.id} style={{ width: isExpanded ? 260 : "100%", flexShrink: 0 }}>
                              {this.renderAlertCard(card)}
                            </div>
                          ))
                        ) : (
                          <div style={{ padding: 20, textAlign: "center", color: "#6b778c", fontSize: 12 }}>
                            No alerts
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <Card style={{ margin: "20px" }}>
              <Empty
                image={<Icon type="inbox" style={{ fontSize: 48, color: "rgba(0,0,0,0.25)" }} />}
                description={
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 4, color: "rgba(0,0,0,0.85)" }}>
                      No alerts found
                    </div>
                    <div style={{ color: "rgba(0,0,0,0.45)" }}>Try clearing filters or adjusting your search.</div>
                  </div>
                }
              />
            </Card>
          )}
        </Content>
      </div>
    );
  }
}
